import React from "react";
import PropTypes from "prop-types";
import { Icon } from "semantic-ui-react";
import { getZendeskTicketUrl } from "../../constants/endpoints";
import {
  getApplicantStatusString,
  getPaymentStatusString,
} from "../../Router/routers/DashboardRouter/routers/Products/constants/applicantStatuses";

function formatDate(dateStr) {
  if (!dateStr) return "--";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const GREEN_STATUSES = [4, 5, 7, 22, 23, 24, 26, 27, 62, 73];
const RED_STATUSES = [0, 8, 12, 17, 20, 38, 43, 47, 48, 51, 79];
const ORANGE_STATUSES = [1, 2, 3, 6, 10, 11, 14, 28, 32, 33, 34, 56, 57, 58, 59, 60, 65, 67, 74, 75, 76, 81, 84];

function getStatusColor(status) {
  if (GREEN_STATUSES.includes(status)) return "wa-badge--green";
  if (RED_STATUSES.includes(status)) return "wa-badge--red";
  if (ORANGE_STATUSES.includes(status)) return "wa-badge--orange";
  return "wa-badge--blue";
}

function getPaymentColor(paymentStatus) {
  if (paymentStatus === 1 || paymentStatus === 8) return "wa-badge--green";
  if (paymentStatus === 4 || paymentStatus === 3 || paymentStatus === 7) return "wa-badge--red";
  return "wa-badge--orange";
}

function CustomerInfoPanel({ applicant, otherApplications, conversation, loading }) {
  if (loading) {
    return (
      <div className="wa-customer-panel">
        <div className="wa-customer-panel__loading">Loading...</div>
      </div>
    );
  }

  if (!applicant) {
    return (
      <div className="wa-customer-panel">
        <div className="wa-customer-panel__header">
          <div className="wa-customer-panel__name">Unknown Customer</div>
          <div className="wa-customer-panel__phone">
            {conversation?.phoneNumber || "--"}
          </div>
        </div>
      </div>
    );
  }

  const fullName =
    [applicant.firstName, applicant.lastName].filter(Boolean).join(" ") ||
    applicant.name ||
    "Unknown";

  const phoneNumbers = applicant.phoneNumbers || [];
  const defaultPhone = phoneNumbers.find((p) => p.default);
  const primaryPhone = defaultPhone?.number || phoneNumbers[0]?.number || null;
  const zendeskUrl =
    applicant.zendeskId
      ? getZendeskTicketUrl(applicant.zendeskId, applicant.zendeskInstance)
      : null;
  const applicantUrl =
    applicant.api && applicant._id
      ? `/${applicant.api}/applicant/${applicant._id}`
      : null;

  const statusLabel = getApplicantStatusString(applicant.status);
  const paymentLabel = getPaymentStatusString(applicant.paymentStatus);

  return (
    <div className="wa-customer-panel">
      <div className="wa-customer-panel__header">
        <div className="wa-customer-panel__name">{fullName}</div>
        <div className="wa-customer-panel__phone">
          {conversation?.phoneNumber || "--"}
        </div>
      </div>

      <div className="wa-customer-panel__section">
        <div className="wa-customer-panel__section-title">
          <span>Identity Details</span>
          <Icon name="id card outline" size="small" />
        </div>
        <div className="wa-customer-panel__field">
          <span className="wa-customer-panel__label">Email Address</span>
          <span className="wa-customer-panel__value">
            {applicant.userEmail || "--"}
          </span>
        </div>
        {primaryPhone && (
          <div className="wa-customer-panel__field">
            <span className="wa-customer-panel__label">Phone</span>
            <span className="wa-customer-panel__value">{primaryPhone}</span>
          </div>
        )}
        <div className="wa-customer-panel__row">
          <div className="wa-customer-panel__field">
            <span className="wa-customer-panel__label">Date of Birth</span>
            <span className="wa-customer-panel__value">
              {formatDate(applicant.dateOfBirth)}
            </span>
          </div>
          <div className="wa-customer-panel__field">
            <span className="wa-customer-panel__label">Passport</span>
            <span className="wa-customer-panel__value">
              {applicant.passportNumber || "--"}
            </span>
          </div>
        </div>
      </div>

      <div className="wa-customer-panel__section">
        <div className="wa-customer-panel__section-title">
          <span>Application</span>
          <Icon name="file alternate outline" size="small" />
        </div>
        <div className="wa-customer-panel__row">
          <div className="wa-customer-panel__field">
            <span className="wa-customer-panel__label">Product</span>
            <span className="wa-customer-panel__value wa-customer-panel__value--bold">
              {(applicant.api || "--").toUpperCase()}
            </span>
          </div>
          <div className="wa-customer-panel__field">
            <span className="wa-customer-panel__label">Order ID</span>
            <span className="wa-customer-panel__value">
              {applicant.orderId ? `#${applicant.orderId}` : "--"}
            </span>
          </div>
        </div>
        <div className="wa-customer-panel__row">
          <div className="wa-customer-panel__field">
            <span className="wa-customer-panel__label">Status</span>
            <span
              className={`wa-badge ${getStatusColor(applicant.status)}`}
            >
              {statusLabel}
            </span>
          </div>
          <div className="wa-customer-panel__field">
            <span className="wa-customer-panel__label">Payment</span>
            <span
              className={`wa-badge ${getPaymentColor(applicant.paymentStatus)}`}
            >
              {paymentLabel}
            </span>
          </div>
        </div>
        {applicant.zendeskId && (
          <div className="wa-customer-panel__field">
            <span className="wa-customer-panel__label">Zendesk ID</span>
            <div className="wa-customer-panel__zendesk-row">
              <span className="wa-customer-panel__value wa-customer-panel__value--green">
                {applicant.zendeskId}
              </span>
              {zendeskUrl && (
                <a
                  href={zendeskUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="wa-customer-panel__ext-link"
                >
                  <Icon name="external alternate" size="small" />
                </a>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="wa-customer-panel__actions">
        {applicantUrl && (
          <a href={applicantUrl} target="_blank" rel="noopener noreferrer" className="wa-customer-panel__cta">
            <Icon name="users" />
            Open Application
          </a>
        )}
        {zendeskUrl && (
          <a
            href={zendeskUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="wa-customer-panel__cta wa-customer-panel__cta--outline"
          >
            <Icon name="external alternate" />
            Open Zendesk Ticket
          </a>
        )}
      </div>

      {otherApplications && otherApplications.length > 0 && (
        <div className="wa-customer-panel__section">
          <div className="wa-customer-panel__section-title">
            <span>Other Applications ({otherApplications.length})</span>
            <Icon name="history" size="small" />
          </div>
          {otherApplications.map((app) => (
            <div key={app._id} className="wa-customer-panel__other-app">
              <div className="wa-customer-panel__other-app-top">
                <div className="wa-customer-panel__other-app-name">
                  {app.name}
                </div>
                <div className="wa-customer-panel__other-app-actions">
                  {app.api && app._id && (
                    <a
                      href={`/${app.api}/applicant/${app._id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="wa-customer-panel__other-app-icon"
                      title="Open application"
                    >
                      <Icon name="user" size="small" />
                    </a>
                  )}
                  {app.zendeskId && (
                    <a
                      href={getZendeskTicketUrl(app.zendeskId, app.zendeskInstance)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="wa-customer-panel__other-app-icon"
                      title="Open Zendesk ticket"
                    >
                      <Icon name="external alternate" size="small" />
                    </a>
                  )}
                </div>
              </div>
              <div className="wa-customer-panel__other-app-details">
                <div className="wa-customer-panel__other-app-row">
                  {app.api && (
                    <span className="wa-customer-panel__other-app-product">
                      {app.api.toUpperCase()}
                    </span>
                  )}
                  {app.orderId && (
                    <span className="wa-customer-panel__other-app-order">
                      #{app.orderId}
                    </span>
                  )}
                </div>
                <div className="wa-customer-panel__other-app-row">
                  {app.status != null && (
                    <span className={`wa-badge wa-badge--sm ${getStatusColor(app.status)}`}>
                      {getApplicantStatusString(app.status)}
                    </span>
                  )}
                  {app.paymentStatus != null && (
                    <span className={`wa-badge wa-badge--sm ${getPaymentColor(app.paymentStatus)}`}>
                      {getPaymentStatusString(app.paymentStatus)}
                    </span>
                  )}
                </div>
                {app.zendeskId && (
                  <span className="wa-customer-panel__other-app-zendesk">
                    ZD #{app.zendeskId}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

CustomerInfoPanel.propTypes = {
  applicant: PropTypes.object,
  otherApplications: PropTypes.array,
  conversation: PropTypes.object,
  loading: PropTypes.bool,
};

export default CustomerInfoPanel;
